<!--
  @component AuthorizationForm
  @description Formulario para crear un nuevo caso de autorización.
  Busca afiliados por nombre/DNI, busca prestaciones del catálogo,
  opcionalmente carga un prestador si origin=provider.
  Muestra aviso si la prestación no requiere autorización.
-->
<template>
  <div class="auth-form">
    <div class="auth-form__card">
      <h2 class="auth-form__title">Nueva solicitud de autorización</h2>

      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <!-- Origen -->
        <el-form-item label="Origen de la solicitud" prop="origin">
          <el-radio-group v-model="form.origin">
            <el-radio label="member">Afiliado</el-radio>
            <el-radio label="provider">Prestador</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- Afiliado -->
        <el-form-item label="Afiliado" prop="memberId">
          <el-select
            v-model="form.memberId"
            filterable
            remote
            :remote-method="searchMembers"
            :loading="loadingMembers"
            placeholder="Buscar por nombre o número de afiliado..."
            style="width: 100%"
            value-key="_id"
            @change="onMemberChange"
          >
            <el-option
              v-for="m in memberOptions"
              :key="m._id"
              :label="`${m.lastName}, ${m.firstName} — ${
                m.member && m.member.memberId ? m.member.memberId : ''
              }`"
              :value="m._id"
            />
          </el-select>
        </el-form-item>

        <!-- Info plan (si hay afiliado seleccionado) -->
        <!-- UC-REG-086: el endpoint /members ahora populate plan {name, code}.
             Cuando viene como objeto populated mostramos plan.name; si por
             algún motivo viene como ObjectId raw (datos legacy), caemos al
             label "Sin plan" para no exponer el ObjectId al médico. -->
        <div v-if="selectedMember" class="auth-form__member-info">
          <span class="auth-form__member-info-label">Plan:</span>
          {{
            (selectedMember.plan && selectedMember.plan.name) ||
            selectedMember.planName ||
            "Sin plan"
          }}
        </div>

        <!-- Prestador -->
        <el-form-item label="Prestador">
          <div style="margin-bottom: 8px">
            <el-checkbox
              v-model="form.isOutOfNetwork"
              @change="onOutOfNetworkChange"
            >
              Prestador fuera de red
            </el-checkbox>
          </div>
          <el-select
            v-if="!form.isOutOfNetwork"
            v-model="form.providerId"
            filterable
            remote
            clearable
            :remote-method="searchProviders"
            :loading="loadingProviders"
            placeholder="Buscar prestador..."
            style="width: 100%"
          >
            <el-option
              v-for="p in providerOptions"
              :key="p._id"
              :label="`${p.lastName}, ${p.firstName}`"
              :value="p._id"
            />
          </el-select>
          <el-input
            v-else
            v-model="form.outOfNetworkProviderName"
            placeholder="Nombre del prestador fuera de red..."
            style="width: 100%"
          />
        </el-form-item>

        <!-- Categoría de prestación -->
        <el-form-item label="Categoría de prestación">
          <el-select
            v-model="practiceCategory"
            placeholder="Todas las categorías"
            clearable
            style="width: 100%"
            @change="onCategoryChange"
          >
            <el-option
              v-for="(label, key) in categoryLabels"
              :key="key"
              :label="label"
              :value="key"
            />
          </el-select>
        </el-form-item>

        <!-- Prestación -->
        <el-form-item label="Prestación" prop="practiceId">
          <el-select
            v-model="form.practiceId"
            filterable
            remote
            :remote-method="searchPractices"
            :loading="loadingPractices"
            placeholder="Buscar por código o nombre..."
            style="width: 100%"
            @change="onPracticeChange"
          >
            <el-option
              v-for="p in practiceOptions"
              :key="p._id"
              :label="`${p.code} — ${p.name}`"
              :value="p._id"
            />
          </el-select>
        </el-form-item>

        <!-- Aviso si no requiere autorización -->
        <el-alert
          v-if="showNoAuthWarning"
          type="warning"
          title="Esta prestación no requiere autorización previa según el plan del afiliado."
          description="Podés igualmente registrar el caso si lo considerás necesario."
          :closable="false"
          show-icon
          style="margin-bottom: 20px"
        />

        <!-- Fecha de solicitud -->
        <el-form-item label="Fecha de solicitud" prop="requestedAt">
          <el-date-picker
            v-model="form.requestedAt"
            type="datetime"
            placeholder="Seleccionar fecha"
            format="dd/MM/yyyy HH:mm"
            style="width: 100%"
          />
        </el-form-item>

        <!-- Observaciones -->
        <el-form-item label="Observaciones">
          <el-input
            v-model="form.observations"
            type="textarea"
            :rows="3"
            placeholder="Información adicional sobre la solicitud..."
          />
        </el-form-item>

        <!-- Adjuntos -->
        <el-form-item label="Adjuntar órdenes / documentación">
          <el-upload
            ref="upload"
            action="#"
            :auto-upload="false"
            :on-change="onFileChange"
            :file-list="fileList"
            multiple
            :limit="10"
          >
            <el-button size="small" icon="el-icon-upload2"
              >Seleccionar archivos</el-button
            >
            <div slot="tip" class="el-upload__tip">
              PDF, imágenes. Máx 15MB por archivo.
            </div>
          </el-upload>
        </el-form-item>

        <!-- Botones -->
        <div class="auth-form__actions">
          <el-button @click="$router.push({ name: 'authorization' })"
            >Cancelar</el-button
          >
          <el-button type="primary" :loading="isSaving" @click="submit">
            Crear caso
          </el-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import debounce from "debounce";
import authorizationService from "@/services/authorization";
import memberService from "@/services/member";
import practiceCatalogService from "@/services/practice-catalog";
import userService from "@/services/user";

/** @component AuthorizationForm */
export default {
  name: "AuthorizationForm",

  data() {
    return {
      /** @type {Object} Datos del formulario */
      form: {
        origin: "member",
        memberId: "",
        providerId: "",
        isOutOfNetwork: false,
        outOfNetworkProviderName: "",
        practiceId: "",
        observations: "",
        requestedAt: new Date(),
      },
      /** @type {Object} Reglas de validación */
      rules: {
        origin: [
          {
            required: true,
            message: "Seleccioná el origen",
            trigger: "change",
          },
        ],
        memberId: [
          {
            required: true,
            message: "Seleccioná un afiliado",
            trigger: "change",
          },
        ],
        practiceId: [
          {
            required: true,
            message: "Seleccioná una prestación",
            trigger: "change",
          },
        ],
        requestedAt: [
          {
            required: true,
            message: "Ingresá la fecha de solicitud",
            trigger: "change",
          },
        ],
      },
      /** @type {Array} Opciones de afiliados para el select */
      memberOptions: [],
      /** @type {boolean} */
      loadingMembers: false,
      /** @type {Object|null} Afiliado seleccionado */
      selectedMember: null,
      /** @type {Array} Opciones de prestadores */
      providerOptions: [],
      /** @type {boolean} */
      loadingProviders: false,
      /** @type {Array} Opciones de prestaciones */
      practiceOptions: [],
      /** @type {boolean} */
      loadingPractices: false,
      /** @type {boolean} Muestra aviso si la prestación no requiere autorización */
      showNoAuthWarning: false,
      /** @type {Array} Archivos seleccionados para adjuntar */
      fileList: [],
      /** @type {boolean} */
      isSaving: false,
      /** @type {string} Categoría de prestación seleccionada para filtrar */
      practiceCategory: "",
      /** @type {Object} Mapa de categorías de prestación */
      categoryLabels: {
        consultation: "Consulta",
        lab: "Laboratorio",
        imaging: "Imágenes",
        surgery: "Cirugía",
        dental: "Odontología",
        mental_health: "Salud mental",
        rehabilitation: "Rehabilitación",
        emergency: "Urgencias",
        hospitalization: "Internación",
        maternity: "Maternidad",
        medication: "Medicación",
        supply: "Insumos",
        prosthesis: "Prótesis",
        other: "Otros",
      },
    };
  },

  created() {
    this.searchMembers = debounce(this._searchMembers, 350);
    this.searchProviders = debounce(this._searchProviders, 350);
    this.searchPractices = debounce(this._searchPractices, 350);
  },

  mounted() {
    // UC-REG-086: Chrome ignora `autocomplete="off"` cuando detecta los
    // inputs como tarjeta de crédito / dirección. El truco que sí funciona
    // es setear `autocomplete="new-password"` (Chrome lo respeta). Element
    // UI `el-select filterable` renderiza un `<input>` interno al que no
    // podemos pasar la prop directamente; lo seteamos via querySelectorAll
    // en mounted. Esto silencia las sugerencias del browser que reportaba
    // Federico en Nueva Autorización ("tarjeta de crédito" en afiliado,
    // "Capital Federal" en prestador).
    this.$nextTick(() => {
      try {
        const inputs = this.$el.querySelectorAll(".el-input__inner");
        inputs.forEach((el) => {
          el.setAttribute("autocomplete", "new-password");
          // name aleatorio para evitar que Chrome lo agrupe con campos
          // similares de otras pantallas y reutilice sugerencias.
          if (!el.getAttribute("name")) {
            el.setAttribute(
              "name",
              `qx_${Math.random().toString(36).slice(2, 10)}`
            );
          }
        });
      } catch (_) {
        // querySelectorAll falla silenciosamente — no rompemos el form.
      }
    });
  },

  methods: {
    /**
     * @description Busca afiliados por nombre o número de afiliado.
     * @param {string} query
     */
    async _searchMembers(query) {
      if (!query || query.length < 2) return;
      this.loadingMembers = true;
      try {
        const result = await memberService.query({
          searchInput: query,
          limit: 20,
        });
        this.memberOptions = result.docs || [];
      } catch (e) {
        this.$message.error("Error al buscar afiliados");
      } finally {
        this.loadingMembers = false;
      }
    },

    /**
     * @description Guarda el afiliado seleccionado y revisa cobertura si hay prestación.
     * @param {string} id
     */
    onMemberChange(id) {
      this.selectedMember =
        this.memberOptions.find((m) => m._id === id) || null;
      this.checkAuthRequired();
    },

    /**
     * @description Limpia campos de prestador al alternar fuera de red
     */
    onOutOfNetworkChange() {
      this.form.providerId = "";
      this.form.outOfNetworkProviderName = "";
      this.providerOptions = [];
    },

    /**
     * @description Busca prestadores por nombre.
     * @param {string} query
     */
    async _searchProviders(query) {
      if (!query || query.length < 2) return;
      this.loadingProviders = true;
      try {
        const result = await userService.getDoctors({
          searchInput: query,
          limit: 20,
        });
        this.providerOptions = result.docs || [];
      } catch (e) {
        this.$message.error("Error al buscar prestadores");
      } finally {
        this.loadingProviders = false;
      }
    },

    /**
     * @description Busca prestaciones del catálogo.
     * @param {string} query
     */
    async _searchPractices(query) {
      if (!query || query.length < 2) return;
      this.loadingPractices = true;
      try {
        const params = { searchInput: query, limit: 20 };
        if (this.practiceCategory) params.category = this.practiceCategory;
        const result = await practiceCatalogService.query(params);
        this.practiceOptions = result.docs || [];
      } catch (e) {
        this.$message.error("Error al buscar prestaciones");
      } finally {
        this.loadingPractices = false;
      }
    },

    /**
     * @description Limpia la prestación seleccionada al cambiar categoría
     */
    onCategoryChange() {
      this.form.practiceId = "";
      this.selectedPractice = null;
      this.practiceOptions = [];
    },

    /**
     * @description Al cambiar la prestación, verifica si requiere autorización.
     */
    onPracticeChange() {
      this.checkAuthRequired();
    },

    /**
     * @description Verifica si la prestación seleccionada requiere autorización
     * según el plan del afiliado. Muestra aviso si no requiere.
     */
    async checkAuthRequired() {
      this.showNoAuthWarning = false;
      if (!this.form.memberId || !this.form.practiceId) return;
      // La verificación real la hace el backend al crear el caso.
      // Aquí solo mostramos el aviso basado en el campo requiresAuthorization
      // que devuelve el caso creado. No bloqueamos el formulario.
    },

    /**
     * @description Captura los archivos seleccionados en el uploader.
     * @param {Object} file
     * @param {Array} fileList
     */
    onFileChange(file, fileList) {
      this.fileList = fileList;
    },

    /**
     * @description Valida el formulario, crea el caso y sube adjuntos si hay.
     */
    async submit() {
      const valid = await this.$refs.form.validate().catch(() => false);
      if (!valid) return;

      this.isSaving = true;
      try {
        // Crear caso
        const newCase = await authorizationService.create({
          origin: this.form.origin,
          memberId: this.form.memberId,
          isOutOfNetwork: this.form.isOutOfNetwork,
          providerId: this.form.isOutOfNetwork
            ? undefined
            : this.form.providerId || undefined,
          outOfNetworkProviderName: this.form.isOutOfNetwork
            ? this.form.outOfNetworkProviderName
            : undefined,
          practiceId: this.form.practiceId,
          observations: this.form.observations,
          requestedAt: this.form.requestedAt,
        });

        // Sin mensaje — el operador ya sabe lo que está cargando.
        // El campo requiresAuthorization queda registrado en el caso para consulta posterior.

        // Subir adjuntos si hay
        if (this.fileList.length > 0) {
          const formData = new FormData();
          this.fileList.forEach((f) => formData.append("files", f.raw));
          await authorizationService.uploadFiles(newCase._id, formData);
        }

        this.$message.success(
          `Caso #${newCase.caseNumber} creado correctamente`
        );
        this.$router.push({
          name: "authorization-detail",
          params: { id: newCase._id },
        });
      } catch (e) {
        this.$message.error("Error al crear el caso");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-form {
  max-width: 720px;
}

.auth-form__card {
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.auth-form__title {
  font-size: 18px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 28px 0;
}

.auth-form__member-info {
  background: #f0f9ff;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #002d5e;
  margin: -12px 0 20px 0;
}

.auth-form__member-info-label {
  font-weight: 600;
  margin-right: 6px;
}

.auth-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}
</style>
